import {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import sdk from "@farcaster/frame-sdk";
import { Context } from "@farcaster/frame-sdk";
import { useDynamic } from "./DynamicContext";
import { useDynamicContext, Wallet } from "@dynamic-labs/sdk-react-core";
import { useNavigate } from "react-router-dom";
import { logOut, getUser } from "../api";

// Define the context value shape
export interface UserContextType {
  user: UserObject | null;
  isAuthenticated?: boolean;
  isLoadingUser?: boolean;
  refetchUser: () => void;
  logoutFn: (shouldRedirect?: boolean) => void;
  context: Context.FrameContext | null;
  primaryWallet: Wallet | null;
  userWallets: Wallet[];
}

export const DEFAULT_CONTEXT_VALUE = {
  user: null,
  isAuthenticated: false,
  isLoadingUser: false,
  refetchUser: () => null,
  logoutFn: () => null,
  context: null,
  primaryWallet: null,
  userWallets: [],
};

// Create a context with default value
export const UserContext = createContext<UserContextType | undefined>(
  DEFAULT_CONTEXT_VALUE,
);

// Create a provider component
interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const {
    user,
    setUser,
    isLoadingUser,
    setIsLoadingUser,
    primaryWallet,
    userWallets,
  } = useDynamic();
  const [context, setContext] = useState<Context.FrameContext | null>(null);
  const { handleLogOut } = useDynamicContext();
  const navigate = useNavigate();

  const logoutFn = async (shouldRedirect: boolean = true) => {
    try {
      // Clear Dynamic session first
      if (handleLogOut) {
        await handleLogOut();
      }

      // Clear our session
      await logOut();
      setUser(null);
      if (shouldRedirect) {
        navigate("/");
      }
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const fetchUser = useCallback(async () => {
    try {
      setIsLoadingUser(true);
      const data = await getUser();
      setUser(data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoadingUser(false);
    }
  }, [setIsLoadingUser, setUser]);

  // Load the Farcaster Frame SDK
  const loadFrameSdk = async () => {
    setContext(await sdk.context);
    sdk.actions.ready();
  };

  useEffect(() => {
    loadFrameSdk();
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoadingUser,
        refetchUser: fetchUser,
        logoutFn,
        context,
        primaryWallet,
        userWallets,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Create a custom hook to use the UserContext
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
